"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import GuideView from "@/components/optilase/hub/guide-view";
import { surfaceFrom } from "@/components/optilase/surface";
import { buttonVariant } from "@/lib/button-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type GuideTile = SbBlokData & {
  question?: string;
  link_label?: string;
  link?: StoryblokLink;
};

type GuideBlok = SbBlokData & {
  title?: string;
  description?: string;
  tiles?: GuideTile[];
  panel_heading?: string;
  panel_description?: string;
  panel_features?: string;
  panel_button_label?: string;
  panel_button_link?: StoryblokLink;
  panel_button_action?: string;
  panel_button_color?: string;
  bg_color?: unknown;
};

export default function OptilaseGuide({ blok }: { blok: GuideBlok }) {
  const region = regionFromPathname(usePublicPathname());

  const points = (blok.panel_features ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div {...storyblokEditable(blok)}>
      <GuideView
        heading={blok.title ?? ""}
        intro={blok.description || undefined}
        surface={surfaceFrom(blok.bg_color)}
        tiles={(blok.tiles ?? []).map((tile) => ({
          question: tile.question ?? "",
          answerLabel: tile.link_label ?? "",
          href: resolveLink(tile.link, region).href,
        }))}
        panel={
          blok.panel_heading
            ? {
                heading: blok.panel_heading,
                body: blok.panel_description || undefined,
                points,
                ctaLabel: blok.panel_button_label ?? "",
                ctaHref: resolveLink(blok.panel_button_link, region).href,
                ctaAction: blok.panel_button_action,
                ctaVariant: buttonVariant(blok.panel_button_color, "orange"),
              }
            : undefined
        }
      />
    </div>
  );
}
